import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Modal } from 'flowbite-react';
import { Project, Quote } from '../../models';
import QuotesApi from '../../services/Quotes';
import { Button, ClientSelectField, SelectField } from '..';
import { RootState, useAppDispatch, useAppSelector, loadProjects } from '../../redux';

export type QuoteDuplicateModalProps = {
    show: boolean;
    quote: Quote;
    onClose: () => void;
    onDuplicated: (quote: Quote) => void;
};

function QuoteDuplicateModal(props: QuoteDuplicateModalProps) {
    const { show, quote, onClose, onDuplicated } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { projects } = useAppSelector((state: RootState) => state.projects);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show)
            dispatch(loadProjects());
    }, [dispatch, show]);

    const projectOptions = useMemo(() => (
        (projects || []).map((project: Project) => ({
            value: project._id,
            label: project.name,
        }))
    ), [projects]);

    const initialValues = useMemo(() => ({
        name: `${quote.name || ''} (copie)`.trim(),
        client: quote.client || null,
        project: quote.project || null,
    }), [quote]);

    if (!show)
        return null;

    return (
        <Modal show={show} onClose={onClose} size="lg">
            <Modal.Header>{t('quotes.duplicate')}</Modal.Header>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                validate={(values) => {
                    const invalid: AnyObj = {};
                    if (!String(values.name || '').trim())
                        invalid.name = t('common.validation.this_field_is_required');
                    if (!values.client?._id)
                        invalid.client = t('common.validation.this_field_is_required');
                    if (!(values.project?._id || values.project))
                        invalid.project = t('common.validation.this_field_is_required');
                    return invalid;
                }}
                onSubmit={async (values) => {
                    setError('');
                    setSubmitting(true);
                    try {
                        const projectId = values.project?._id || values.project;
                        const json = await QuotesApi.duplicateFrom(quote._id, {
                            client: values.client._id,
                            project: projectId,
                            name: String(values.name || '').trim(),
                        });
                        onDuplicated(json.quote);
                    } catch (err: unknown) {
                        setError(err instanceof Error
                            ? err.message
                            : String(t('quotes.duplicate_failed') || ''));
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        <Modal.Body>
                            <div className="space-y-4">
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {t('quotes.duplicate_help')}
                                </p>
                                {error && (
                                    <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
                                        {error}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">
                                        {t('quotes.name')}
                                    </label>
                                    <Field
                                        type="text"
                                        name="name"
                                        className="block w-full rounded-md border border-gray-300 dark:border-gray-600 py-1.5 text-gray-900 dark:text-gray-100 sm:text-sm"
                                    />
                                    <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">
                                        {t('quotes.project')}
                                    </label>
                                    <div className="border rounded-md dark:border-gray-600">
                                        <SelectField
                                            name="project._id"
                                            placeholder={t('changes.choose_a_project') || ''}
                                            options={projectOptions}
                                            onChange={(value: string) => {
                                                const project = (projects || []).find((p: Project) => p._id === value) || null;
                                                setFieldValue('project', project);
                                            }}
                                        />
                                    </div>
                                    <ErrorMessage name="project" component="div" className="mt-1 text-sm text-red-600" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-gray-100">
                                        {t('quotes.client')}
                                    </label>
                                    <ClientSelectField
                                        name="client"
                                        placeholder={t('changes.choose_a_client') || ''}
                                        value={values.client || undefined}
                                        resultsInFlow
                                    />
                                    <ErrorMessage name="client" component="div" className="mt-1 text-sm text-red-600" />
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                title={t('common.cancel')}
                                color="secondary"
                                onClick={onClose}
                                disabled={submitting}
                            />
                            <Button
                                title={t('quotes.duplicate_confirm')}
                                color="primary"
                                icon="copy"
                                type="submit"
                                loading={submitting}
                            />
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

export default QuoteDuplicateModal;
